"use client";

import React from "react";
import { Tag, Folder } from "lucide-react";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="aspect-square rounded-lg bg-muted animate-pulse" />
        <div className="space-y-6">
          <div className="h-10 w-3/4 bg-muted rounded-md animate-pulse" />
          <div className="h-8 w-1/3 bg-muted rounded-md animate-pulse" />
          <div className="h-24 w-full bg-muted rounded-md animate-pulse" />
          <div className="h-12 w-full bg-muted rounded-md animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-blue-900 mb-4 font-default">Unable to load seafood details.</p>
        <button onClick={refetch} className="underline text-blue-700 font-default">
          Try again
        </button>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-blue-900 font-default">Product not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    Number(product.discounted_price) > 0 &&
    Number(product.discounted_price) !== Number(product.price);

  const regularPrice = Number(product.price);
  const salePrice = hasDiscount ? Number(product.discounted_price) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
      {}
      <div className="relative">
        <div className="relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-md">
          <div className="aspect-square relative">
            {product.featured_image_url ? (
              <img
                src={product.featured_image_url}
                alt={product.title}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-100">
                <span className="text-slate-500 font-default">No image available</span>
              </div>
            )}
            {}
            <div className="absolute top-5 left-5">
              <div
                className="inline-flex items-center justify-center px-4 py-1.5 rounded-md bg-blue-600 text-white border border-blue-700 shadow-sm"
              >
                <span className="text-xs uppercase tracking-widest font-default font-bold">
                  Wild Caught
                </span>
              </div>
            </div>
            {hasDiscount && (
              <div className="absolute top-5 right-5">
                <Badge
                  variant="default"
                  className="bg-blue-800 text-white border-none text-xs uppercase tracking-wider px-3 py-1 rounded-md"
                >
                  Sale
                </Badge>
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      <div className="space-y-6">
        {}
        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Folder className="size-4 text-blue-700" />
            {product.categories.map((cat, i) => (
              <Link
                to={cat.link || "#"}
                key={cat.id}
                data-index={i}
                className="text-xs uppercase tracking-widest text-blue-900 hover:text-blue-600 transition-colors font-default"
              >
                {cat.name}
                {i < product.categories.length - 1 ? "," : ""}
              </Link>
            ))}
          </div>
        )}

        {}
        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-blue-900 leading-tight tracking-tight font-default"
        >
          {product.title}
        </h1>

        {}
        <div className="flex items-baseline gap-3 flex-wrap">
          {hasDiscount ? (
            <>
              <span
                className="text-3xl md:text-4xl font-bold text-blue-900 font-default"
              >
                ${salePrice?.toFixed(2)}
              </span>
              <span className="text-xl text-slate-500 line-through font-default">
                ${regularPrice.toFixed(2)}
              </span>
            </>
          ) : (
            <span
              className="text-3xl md:text-4xl font-bold text-blue-900 font-default"
            >
              ${regularPrice.toFixed(2)}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="text-base md:text-lg text-slate-700 leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        <Separator className="bg-slate-200" />

        {}
        <div className="space-y-4">
          <AddToCartButton
            productId={product.id}
            variant="default"
            size="lg"
            className="w-full"
            buttonClassName="bg-blue-600 text-white hover:bg-blue-700 rounded-md"
            text="Add to Cart"
            showQuantity={true}
          />
        </div>

        <Separator className="bg-slate-200" />

        {}
        {product.long_description && (
          <div className="pt-2">
            <h2
              className="text-2xl font-bold text-blue-900 mb-4 font-default"
            >
              Product Details
            </h2>
            <div
              className="prose prose-sm md:prose-base max-w-none text-slate-800 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_a]:text-blue-600 [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              dangerouslySetInnerHTML={{ __html: product.long_description }}
            />
          </div>
        )}

        {}
        {product.tags && product.tags.length > 0 && (
          <>
            <Separator className="bg-slate-200" />
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Tag className="size-4 text-blue-700" />
              <span className="text-sm uppercase tracking-widest text-slate-600 font-default mr-2">
                Tags:
              </span>
              {product.tags.map((tag, i) => (
                <Link
                  to={tag.link || "#"}
                  key={tag.id}
                  data-index={i}
                  className="inline-flex items-center px-3 py-1 rounded-md border border-slate-200 bg-white text-xs uppercase tracking-wider text-blue-900 hover:bg-blue-50 transition-colors font-default"
                >
                  {tag.name}
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Product() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="1521"
      id="single-product-template-section"
      className="relative w-full bg-slate-50 py-16 md:py-24 overflow-hidden"
    >
      {}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className="mb-8 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-blue-800 font-default">
          <Link to="/HomePage" className="hover:text-blue-600 transition-colors">
            Home
          </Link>
          <span>/</span>
          <Link to="/ShopPage" className="hover:text-blue-600 transition-colors">
            Shop
          </Link>
          <span>/</span>
          <span className="text-blue-900">Seafood Selection</span>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}